<script setup lang="ts">
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import { StackLayout, TitleTypography } from '@rfc/rfc-components-library'
import type { Position } from '@vueuse/core'
import { ref, watch } from 'vue'
import type { FFDragEvent } from '../../../../../types/Firefox/DragEvent'
import CheckpointDraggableCard from './CheckpointDraggableCard.vue'

type Props = {
    data: CheckpointModel[]
}
const props = defineProps<Props>()

type Emits = {
    (e: 'setCheckpointsOrder', value: number[]): void
}
const emit = defineEmits<Emits>()

const checkpointOrder = ref<CheckpointModel[]>(props.data)
watch(
    () => checkpointOrder.value,
    () =>
        checkpointOrder.value.sort((itemA, itemB) => {
            if (!itemA.data?.order || !itemB.data?.order) return 0
            return itemA.data?.order < itemB.data?.order ? -1 : 1
        })
)

const checkpointDragRef = ref<{ ref: { handle: HTMLDivElement } }[] | null>()
const currentIndex = ref<number | undefined>(undefined)
const draggingCheckpoint = ref<CheckpointModel | undefined>()

const isDragging = ref(false)

const handleDragEnd = (
    checkpoint: CheckpointModel,
    position?: Position,
    event?: FFDragEvent | TouchEvent
) => {
    if (event?.type === 'touchend' && position) {
        const documentKey = document
            .elementFromPoint(position?.x, position?.y)
            ?.id.split('-')
            .at(-1)
        currentIndex.value = documentKey ? parseInt(documentKey) : undefined
    }
    isDragging.value = false
    if (currentIndex.value === undefined || currentIndex.value === null) return

    const currentCpIndex = checkpointOrder.value.findIndex(item => checkpoint.id === item.id)
    if (currentIndex.value === currentCpIndex || currentIndex.value === currentCpIndex + 1)
        return (currentIndex.value = undefined)

    const movedCp = checkpointOrder.value.splice(currentCpIndex, 1)
    checkpointOrder.value.splice(
        currentCpIndex < currentIndex.value ? currentIndex.value - 1 : currentIndex.value,
        0,
        movedCp[0]
    )
    checkpointOrder.value = checkpointOrder.value.map((item, index) => ({
        ...item,
        data: { ...(item.data ?? {}), order: index }
    }))
    emit(
        'setCheckpointsOrder',
        checkpointOrder.value.map(item => item.id)
    )
}

const handleDragStart = (value: CheckpointModel, event?: any) => {
    draggingCheckpoint.value = value
    isDragging.value = true
}

const addDragover = (e: DragEvent | TouchEvent, key: number) => {
    ;(e.currentTarget as Element)?.classList.add('dragover')
    currentIndex.value = key
}
const removeDragover = (e: DragEvent | TouchEvent) =>
    (e.currentTarget as Element)?.classList.remove('dragover')
</script>

<template>
    <StackLayout direction="column" :gap="16" class="relative">
        <template v-for="(checkpoint, key) in checkpointOrder" :key="key">
            <div class="relative align-self-center">
                <div
                    v-if="key === 0"
                    id="hr-key-0"
                    @dragenter.passive="addDragover($event, 0)"
                    @dragleave.passive="removeDragover($event)"
                    class="hr-separator-first-zone">
                    <hr class="hr-separation top-[10px] absolute" />
                </div>
                <StackLayout class="align-center align-self-center relative" :gap="8">
                    <TitleTypography :title="`${key + 1}.`" />
                    <CheckpointDraggableCard
                        ref="checkpointDragRef"
                        :data="checkpoint"
                        @dragStart="handleDragStart"
                        @dragEnd="(val1, val2) => handleDragEnd(checkpoint, val1, val2)" />
                </StackLayout>
                <div
                    :id="`hr-key-${key + 1}`"
                    @dragenter.passive="addDragover($event, key + 1)"
                    @dragleave.passive="removeDragover($event)"
                    dropzone
                    class="absolute align-self-center hr-separator-zone">
                    <hr class="hr-separation" />
                </div>
            </div>
        </template>
    </StackLayout>
</template>

<style scoped lang="scss">
hr {
    align-self: center;
    width: 300px;
    border: 1px dashed rgb(var(--v-theme-primary));
}

.hr-separator-first-zone {
    position: absolute;
    align-self: center;
    width: 100%;
    height: 40px;
    &.dragover {
        & .hr-separation {
            border: 1px dashed rgb(var(--v-theme-success));
        }
    }
    &:hover {
        & .hr-separation {
            border: 1px dashed rgb(var(--v-theme-success));
        }
    }
}

.hr-separator-zone {
    height: 60px;
    transform: translateY(9px);
    align-content: center;

    &.dragover {
        & .hr-separation {
            border: 1px dashed rgb(var(--v-theme-success));
        }
    }
    &:hover {
        & .hr-separation {
            border: 1px dashed rgb(var(--v-theme-success));
        }
    }
}
.hr-separation {
    pointer-events: none;
    border: 1px dashed rgb(var(--v-theme-primary));
}
</style>
